import { Typography } from 'antd';
import type { CustomTagProps } from 'rc-select/lib/BaseSelect';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ConfigurationFieldStatus } from '@ydistri/utils';
import ConfigurationMultiselect from '../../global/ConfigurationMultiselect/ConfigurationMultiselect';
import type {
  MultiselectCustomerPartialEntity,
  ResolvedPastedData,
} from '../../global/ConfigurationMultiselect/configurationMultiselectTypes';
import { useLookupScopeProductsMutation } from '../../../apis/apiScopes';
import { toScopeProductOptions } from './scopeProductOptions';

export interface ScopeProductMultiselectProps {
  data: MultiselectCustomerPartialEntity[];
  disabled?: boolean;
  loading?: boolean;
  resolutionContextKey: React.Key;
  tagRender?: (props: CustomTagProps) => React.ReactElement;
  value: number[];
  onOptionsResolved: (data: MultiselectCustomerPartialEntity[]) => void;
  onValueChange: (value: number[]) => void;
}

const ScopeProductMultiselect: React.FC<ScopeProductMultiselectProps> = ({
  data,
  disabled = false,
  loading = false,
  resolutionContextKey,
  tagRender,
  value,
  onOptionsResolved,
  onValueChange,
}) => {
  const [lookupProducts, { isLoading: isLookingUp }] = useLookupScopeProductsMutation();
  const requestedProductIdsRef = useRef(new Set<number>());
  const isMountedRef = useRef(true);
  const [isHydrating, setIsHydrating] = useState(false);
  const [hydrationError, setHydrationError] = useState<string>();

  const resolvePastedData = useCallback(
    async (
      identifiers: string[],
    ): Promise<ResolvedPastedData<MultiselectCustomerPartialEntity>> => {
      const response = await lookupProducts({ identifiers }).unwrap();
      return {
        data: toScopeProductOptions(response.products),
        notRecognized: response.unrecognizedIdentifiers,
      };
    },
    [lookupProducts],
  );

  const onResolvedPastedData = useCallback(
    (resolvedData: MultiselectCustomerPartialEntity[]) => {
      onOptionsResolved(resolvedData);
    },
    [onOptionsResolved],
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const knownProductIds = new Set(data.map(option => option.id));
    const missingProductIds = [...new Set(value)].filter(
      productId =>
        Number.isSafeInteger(productId) &&
        productId > 0 &&
        !knownProductIds.has(productId) &&
        !requestedProductIdsRef.current.has(productId),
    );
    if (missingProductIds.length === 0) return;

    missingProductIds.forEach(productId => requestedProductIdsRef.current.add(productId));
    setHydrationError(undefined);
    setIsHydrating(true);

    void (async () => {
      try {
        const response = await lookupProducts({ productIds: missingProductIds }).unwrap();
        if (!isMountedRef.current) return;
        onOptionsResolved(toScopeProductOptions(response.products));
      } catch {
        if (isMountedRef.current) {
          setHydrationError('Unable to load selected Product details.');
        }
      } finally {
        if (isMountedRef.current) {
          setIsHydrating(false);
        }
      }
    })();
  }, [data, lookupProducts, onOptionsResolved, value]);

  return (
    <>
      <ConfigurationMultiselect
        data={data}
        disabled={disabled}
        loading={loading === true || isLookingUp || isHydrating}
        maxTagCount={5}
        placeholder="Paste product IDs or codes to select products"
        popupMatchSelectWidth={false}
        resolutionContextKey={resolutionContextKey}
        showSelectionActions={false}
        size="normal"
        status={ConfigurationFieldStatus.DEFAULT}
        tagRender={tagRender}
        value={value}
        resolvePastedData={resolvePastedData}
        onResolvedPastedData={onResolvedPastedData}
        onValueChange={onValueChange}
      />
      {hydrationError && <Typography.Text type="danger">{hydrationError}</Typography.Text>}
    </>
  );
};

export default ScopeProductMultiselect;
